/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICrossEntityInventoryNode } from '@msdyn365-commerce-modules/retail-actions';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { action, computed, observable } from 'mobx';

import { IWarehouseLocatorStateContext, IWarehouseLocatorStateManager } from './i-warehouse-locator-state-manager';

/**
 * Default observable implementation of {@link IWarehouseLocatorStateManager}.
 * The dialog is considered open whenever a context has been set.
 */
export class WarehouseLocatorStateManager implements IWarehouseLocatorStateManager {
    @computed public get isDialogOpen(): Readonly<boolean> {
        return this._context !== undefined;
    }

    @computed public get context(): Readonly<IWarehouseLocatorStateContext | undefined> {
        return this._context;
    }

    @computed public get product(): Readonly<SimpleProduct | undefined> {
        return this._context?.product;
    }

    @observable public nodes?: ICrossEntityInventoryNode[];

    @observable public selectedNodeKey?: string;

    @observable protected _context?: IWarehouseLocatorStateContext;

    @action
    public async openDialog(input: IWarehouseLocatorStateContext): Promise<void> {
        this._context = input;
    }

    @action
    public async closeDialog(): Promise<void> {
        this._context = undefined;
        // Clear plotted state as well. Leaving nodes behind would let a closed warehouse dialog
        // keep taking precedence in map modules shared with the store/distributor selectors.
        this.nodes = undefined;
        this.selectedNodeKey = undefined;
    }

    @action
    public async setNodes(nodes: ICrossEntityInventoryNode[]): Promise<void> {
        this.nodes = nodes;
    }

    @action
    public async setSelectedNodeKey(nodeKey: string | undefined): Promise<void> {
        this.selectedNodeKey = nodeKey;
    }
}

export default WarehouseLocatorStateManager;
